import React, { useEffect, useState } from 'react';
import { AlertCircle, Coins } from 'lucide-react';
import { useToast } from '../../../../../hooks/useToast';
import { BankAccountRecord, listBankAccounts } from '../../../../../services/BankAccountsService';
import { ChartAccount, listChartOfAccounts } from '../../../../../services/ChartOfAccountsService';
import { JournalLine, postJournalEntry } from '../../../../../services/JournalEntriesService';
import { listSuppliers } from '../../../../../services/SuppliersService';
import { ensureSupplierAccount } from '../../../../../services/SupplierAccountsService';
import { Supplier } from '../../../../../types/Supplier.types';
import { formatEuro } from '../masters/financeMasters';
import { FormSkeleton, JournalPreview, SearchSelect, today } from './FormParts';
import '../../shared-modules.css';
import './FinanceForms.css';

const round2 = (n: number) => Math.round(n * 100) / 100;

interface SupplierPaymentFormProps {
  privacyMode: boolean;
  onDone?: () => void;
}

export const SupplierPaymentForm = ({ privacyMode, onDone }: SupplierPaymentFormProps) => {
  const { notify } = useToast();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [banks, setBanks] = useState<BankAccountRecord[]>([]);
  const [accounts, setAccounts] = useState<ChartAccount[]>([]);

  const [supplierId, setSupplierId] = useState('');
  const [date, setDate] = useState(today());
  const [bankId, setBankId] = useState('');
  const [amount, setAmount] = useState(0);
  const [sepaRef, setSepaRef] = useState('');
  const [notes, setNotes] = useState('');

  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [supplierList, bankList, chart] = await Promise.all([
          listSuppliers({ status: 'active' }),
          listBankAccounts(),
          listChartOfAccounts()
        ]);
        if (cancelled) return;
        setSuppliers(supplierList);
        setBanks(bankList);
        setBankId(bankList.length > 0 ? bankList[0].id : '');
        setAccounts(chart);
        setLoadError('');
      } catch (err) {
        console.error('Error loading supplier payment form data:', err);
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'No se pudieron cargar proveedores y cuentas bancarias.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const supplier = suppliers.find(s => s.id === supplierId);
  const bank = banks.find(b => b.id === bankId);
  const total = round2(amount);

  const journalLines: JournalLine[] = [
    { account: '400000', accountName: 'Proveedores (Euros)', debit: total, credit: 0 },
    { account: '572000', accountName: bank ? `Bancos c/c (${bank.name})` : 'Bancos c/c', debit: 0, credit: total }
  ];

  // Subcuenta 572 del banco elegido: la enlazada a su cuenta bancaria; sin enlace, la 572000 de siempre
  const bankCode = (bank && (bank.accountCode || accounts.find(a => a.id === bank.accountId)?.code)) || '572000';

  const validate = () => {
    if (!supplier) return 'Seleccione un proveedor.';
    if (!date) return 'La fecha es obligatoria.';
    if (!bank) return 'Seleccione la cuenta de origen.';
    if (total <= 0) return 'El importe debe ser mayor que cero.';
    return '';
  };

  const handleSubmit = async () => {
    const error = validate();
    if (error) {
      setFormError(error);
      return;
    }
    setFormError('');
    setSaving(true);
    try {
      const ref = sepaRef.trim() ? ` (SEPA ${sepaRef.trim()})` : '';
      // El pago asienta en la cuenta propia del proveedor (400NNNN o 410NNNN); si todavía no la tiene, se crea ahora.
      // Si no se puede obtener, se asienta en la 400000 genérica y se avisa.
      let supplierCode = '400000';
      let chart = accounts;
      try {
        const own = await ensureSupplierAccount(supplier.id, '400');
        supplierCode = own.code;
        chart = await listChartOfAccounts();
        setAccounts(chart);
      } catch (accountError) {
        notify('Cuenta del proveedor', `No se pudo obtener su cuenta propia (${accountError instanceof Error ? accountError.message : 'error del servidor'}): se asienta en la 400000 genérica.`, 'warning');
      }
      const lines = journalLines.map(l => {
        if (l.account === '400000') return { ...l, account: supplierCode, accountName: `Proveedores (${supplier.name})` };
        if (l.account === '572000') return { ...l, account: bankCode };
        return l;
      });
      await postJournalEntry(`Pago a proveedor ${supplier.name}${ref}`, lines, 'Pagos a Proveedores', chart, date);
    } catch (err) {
      console.error('Error posting supplier payment journal entry:', err);
      setFormError(`No se pudo contabilizar el pago: ${err instanceof Error ? err.message : 'error del servidor.'}`);
      setSaving(false);
      return;
    }
    notify('Pago contabilizado', `Pago de ${formatEuro(total, privacyMode)} a ${supplier.name} contabilizado en el Libro Diario [400 a 572].`, 'info');
    setSaving(false);
    if (onDone) onDone();
  };

  if (loading) return <FormSkeleton />;

  if (loadError) {
    return (
      <div className="module-panel">
        <div className="module-panel-empty">{loadError}</div>
      </div>
    );
  }

  const supplierOptions = suppliers.map(s => ({ value: s.id, label: s.name, sub: s.cif ? `CIF: ${s.cif}` : undefined }));

  return (
    <div className="fin-doc">
      <div className="fin-doc__header">
        <div>
          <span className="fin-doc__tag">COMPRAS Y PAGOS • PAGOS A PROVEEDORES</span>
          <h3 className="fin-doc__title"><Coins size={14} /> Pago a proveedor</h3>
        </div>
        <span className="fin-doc__badge">400000 A 572000</span>
      </div>

      {formError && (
        <div className="module-form-error">
          <AlertCircle size={14} style={{ flexShrink: 0 }} />
          <span>{formError}</span>
        </div>
      )}

      <div className="fin-doc__grid">
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Proveedor (buscador) *</label>
          <SearchSelect
            options={supplierOptions}
            value={supplierId}
            onChange={setSupplierId}
            placeholder="Buscar proveedor por nombre o CIF..."
            emptyText="No hay proveedores"
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Fecha *</label>
          <input
            type="date"
            required
            className="sidebar-form-input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Cuenta origen *</label>
          <select className="sidebar-form-input" value={bankId} onChange={(e) => setBankId(e.target.value)}>
            {banks.length === 0 && <option value="">No hay cuentas bancarias</option>}
            {banks.map(b => <option key={b.id} value={b.id}>572000 · {b.name}</option>)}
          </select>
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Importe (€) *</label>
          <input
            type="number"
            min={0}
            step="0.01"
            className="sidebar-form-input"
            value={amount}
            onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Referencia SEPA</label>
          <input
            type="text"
            className="sidebar-form-input fin-doc__mono"
            value={sepaRef}
            onChange={(e) => setSepaRef(e.target.value)}
            placeholder="Ej. SEPA-TRF-2026-0042"
          />
        </div>
      </div>

      <div className="fin-lines">
        <div className="fin-lines__header">
          <span>Facturas pendientes del proveedor</span>
        </div>
        <div className="fin-lines__row fin-lines__row--head fin-lines__row--collect">
          <span />
          <span>Factura</span>
          <span>Fecha</span>
          <span>Vencimiento</span>
          <span className="right">Pendiente</span>
          <span className="right">Importe aplicado</span>
        </div>
        {/* Sin endpoint de facturas recibidas no hay facturas que listar */}
        <div className="fin-lines__empty">
          Las facturas de proveedor aún no se guardan en el sistema
        </div>
      </div>

      <div className="fin-doc__totals">
        <span className="fin-doc__total">Total pagado: <strong>{formatEuro(total, privacyMode)}</strong></span>
      </div>

      <div className="sidebar-form-group">
        <label className="sidebar-form-label">Notas</label>
        <textarea
          rows={3}
          className="sidebar-form-input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Observaciones sobre el pago..."
        />
      </div>

      <JournalPreview lines={journalLines} privacyMode={privacyMode} />

      <div className="fin-doc__actions">
        <button type="button" className="fin-doc__btn" disabled title="Pendiente de endpoint">
          Guardar
        </button>
        <button type="button" className="fin-doc__btn fin-doc__btn--primary" disabled={saving} onClick={handleSubmit}>
          {saving ? 'Contabilizando...' : 'Ejecutar y contabilizar'}
        </button>
      </div>
    </div>
  );
};

export default SupplierPaymentForm;
